import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ShoppingBag, Loader2, ArrowLeft, Trophy, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MerchandisePage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [redeemResult, setRedeemResult] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [merchRes, userRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/merchandise`),
          fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        ]);
        if (merchRes.ok) setItems(await merchRes.json());
        if (userRes.ok) setUser(await userRes.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const redeemItem = async (item) => {
    if (!user) { navigate('/login'); return; }
    if (!window.confirm(`Redeem ${item.points_cost} points for ${item.discount_percent}% off ${item.name}?`)) return;
    setRedeeming(item.item_id);
    setRedeemResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/merchandise/${item.item_id}/redeem`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setRedeemResult({ success: true, item_id: item.item_id, ...data });
        setUser(prev => prev ? { ...prev, total_points: data.remaining_points } : prev);
      } else {
        setRedeemResult({ success: false, item_id: item.item_id, message: data.detail });
      }
    } catch (e) { setRedeemResult({ success: false, item_id: item.item_id, message: 'Something went wrong' }); }
    finally { setRedeeming(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20" data-testid="merchandise-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <ShoppingBag className="w-6 h-6 text-amber-400" /> Merchandise
              </h1>
              <p className="text-gray-500 text-sm">Exclusive Life Decoder merchandise</p>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-2 bg-[#141414] border border-[#252525] rounded-lg px-4 py-2" data-testid="points-balance">
              <Trophy className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-semibold">{user.total_points || 0}</span>
              <span className="text-gray-500 text-sm">pts</span>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {items.length === 0 ? (
          <Card className="bg-[#141414] border-[#252525]">
            <CardContent className="p-12 text-center">
              <ShoppingBag className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">No merchandise available yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(item => {
              const hasPoints = item.points_cost > 0;
              const canRedeem = user && (user.total_points || 0) >= item.points_cost;
              const thisResult = redeemResult && redeemResult.item_id === item.item_id ? redeemResult : null;

              return (
                <Card key={item.item_id} className="bg-[#141414] border-[#252525] overflow-hidden hover:border-amber-500/30 transition-all group"
                  data-testid={`merch-card-${item.item_id}`}>
                  {/* Image */}
                  <div className="w-full aspect-square bg-[#1a1a1a] overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : item.image_url} alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-16 h-16 text-gray-800" />
                      </div>
                    )}
                  </div>

                  <CardContent className="p-5">
                    {/* Name & Price */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-white font-semibold">{item.name}</h3>
                      <span className="text-amber-400 font-bold text-lg shrink-0">{item.price}</span>
                    </div>

                    {item.description && (
                      <p className="text-sm text-gray-500 mb-4">{item.description}</p>
                    )}

                    {/* Points Redemption */}
                    {hasPoints && (
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-purple-400" />
                            <span className="text-sm text-purple-300">{item.points_cost} pts</span>
                          </div>
                          <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                            {item.discount_percent}% OFF
                          </Badge>
                        </div>
                        {user && (
                          <Button
                            onClick={() => redeemItem(item)}
                            disabled={!canRedeem || redeeming === item.item_id}
                            className={`w-full mt-2 ${canRedeem ? 'bg-purple-600 hover:bg-purple-500' : 'bg-gray-800 cursor-not-allowed'}`}
                            data-testid={`redeem-${item.item_id}`}
                          >
                            {redeeming === item.item_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : canRedeem ? (
                              `Redeem ${item.points_cost} pts for ${item.discount_percent}% off`
                            ) : (
                              `Need ${item.points_cost - (user.total_points || 0)} more pts`
                            )}
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Result message */}
                    {thisResult && (
                      <div className={`rounded-lg p-3 mb-4 text-sm ${thisResult.success ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
                        {thisResult.message}
                      </div>
                    )}

                    {/* WhatsApp Order */}
                    <a
                      href={`https://wa.me/601171383699?text=${encodeURIComponent(
                        thisResult && thisResult.success
                          ? `Hi, I've redeemed ${item.points_cost} points for ${item.discount_percent}% off ${item.name} (${item.price}). I'd like to complete my order!`
                          : `Hi, I'd like to order: ${item.name} (${item.price})`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium rounded-md transition-colors"
                      data-testid={`order-${item.item_id}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {thisResult && thisResult.success ? 'Complete Order via WhatsApp' : 'Order via WhatsApp'}
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchandisePage;
